import { showSocketNotice } from "../ui/serverStatus.ts";
// The real-time game screen (plan §5–6, §13): chessground board + clocks + move
// list + chat + controls, driven by the versioned game socket. Legal moves,
// premove validation and promotion detection use chessops on the client; the
// server remains the sole authority over the clock, flag and result.

import { Chessground } from "chessground";
import type { Api as CgApi } from "chessground/api";
import type { Config as CgConfig } from "chessground/config";
import type { Key } from "chessground/types";

import { api, ApiError } from "../api.ts";
import { resourceRecovery } from "./resourceRecovery.ts";
import { esc, h } from "../dom.ts";
import { navigate, type Params } from "../router.ts";
import { PlayConnection, readPlayer } from "../play/client.ts";
import { saveGamesPanel } from "./myGames.ts";
import { opponentControls } from "../play/moderation.ts";
import { renewGuestOnActivity } from "../play/client.ts";
import { brandLockup, BRAND_NAME } from "../ui/brand.ts";
import { confirmDialog } from "../ui/confirm.ts";
import { onDisconnect } from "../ui/lifecycle.ts";
import { boardSettings } from "../ui/preferences.ts";
import { t } from "../i18n/index.ts";

import { checkColor, isPromotion, legalDests, parsePosition, uciSquares } from "../game/board.ts";
import { ClockUI } from "../game/clock.ts";
import { isPremoveEnabled, onPremoveChange } from "../game/premove.ts";
import { GameSocket } from "../game/ws.ts";
import { MoveReplay } from "../game/replay.ts";
import { play } from "../sound.ts";
import { muteButton } from "../ui/mute.ts";
import { promotionCard } from "../ui/promotion.ts";
import { registerBoard } from "../ui/tabs.ts";
import type {
  ChatOut,
  Color,
  EndOut,
  FullGame,
  GoneOut,
  MoveOut,
  PeerState,
  PresenceOut,
  RatingsOut,
  ServerEnvelope,
} from "../game/types.ts";
import {
  buildReview,
  capturedFor,
  isTypingTarget,
  navControls,
  navKey,
  paintCaptured,
  scrollIntoListView,
  type GameNode,
} from "../game/review.ts";
import { Chess } from "chessops/chess";
import { parseSan } from "chessops/san";
import { makeUci, parseUci } from "chessops/util";

/** Only one game is on screen at a time; tear the previous one down on entry. */
let active: { destroy: () => void } | null = null;

export async function gameScreen(params: Params): Promise<HTMLElement> {
  active?.destroy();
  active = null;

  const id = params.id ?? "";
  let user;
  try { user = await readPlayer(); } catch { return resourceRecovery(`/game/${id}`); }
  if (!user) return resourceRecovery(`/game/${id}`);

  let full: FullGame;
  try {
    full = await api.getGame(id);
  } catch (err) {
    if (err instanceof ApiError && [401,403,404].includes(err.status)) return resourceRecovery(`/game/${id}`);
    return notice(t("game.loadFailed"));
  }

  const myColor: Color | null =
    full.white.id === (full.player_id ?? user.id) ? "white" : full.black.id === (full.player_id ?? user.id) ? "black" : null;

  const el = buildLayout(full, myColor);
  if (myColor) el.querySelector("aside")?.append(saveGamesPanel(undefined,{id,player_id:full[myColor].id}));
  if (myColor) el.querySelector("aside")?.append(opponentControls(id,full[myColor].id,full[myColor==='white'?'black':'white'].id));
  // Board + socket need the element attached to the DOM to size correctly.
  requestAnimationFrame(() => {
    // A superseded navigation's layout never attaches — don't spin up a socket
    // and board for it (onDisconnect would tear it down again immediately).
    if (!el.isConnected) return;
    const ctrl = new GameCtrl(el, id, full, myColor);
    active = ctrl;
    onDisconnect(el, () => {
      document.getElementById("socket-notice")?.remove();
      ctrl.destroy();
      if (active === ctrl) active = null;
    });
  });
  return el;
}

// ---------------------------------------------------------------------------
// Controller
// ---------------------------------------------------------------------------

class GameCtrl {
  private cg: CgApi;
  private replay: MoveReplay;
  private clock: ClockUI;
  private socket?: GameSocket;
  private playConnection?: PlayConnection;
  private seenRevision = 0;
  private clearSocketNotice?: () => void;
  private pos: Chess;
  private serverFen: string;
  private turn: Color;
  private status: string;
  private moves: { uci: string; san: string }[];
  /** Positions for review (node 0 = start, node i = after ply i), rebuilt from
   *  `moves`; shared with the analysis board (`review.ts`). */
  private nodes: GameNode[] = [];
  /** Which ply the board is currently showing; the last node = "live tip". */
  private viewPly = 0;
  /** A move of mine is in flight, awaiting the server echo — nav is frozen. */
  private awaitingEcho = false;
  private mutationsAvailable = false;
  private hasSocketSnapshot = false;
  private connectionLive = false;
  private lifecycle = "waiting";
  private clockMode = "timed";
  private clockRunning = false;
  private importedPlies = 0;
  /** The colour asking to continue in Messages, and who was refused. */
  private returnOffer?: Color;
  private returnRefused: Color[] = [];
  private latestFull?: FullGame;
  private lifecycleTimer?: number;
  private serverTimeOffset = 0;
  private onKey: (e: KeyboardEvent) => void = () => {};
  private lastMoveShownAt = performance.now();
  private goneTimer?: number;
  private unregisterTab: () => void = () => {};
  private offPremove: () => void = () => {};

  constructor(
    private root: HTMLElement,
    private id: string,
    full: FullGame,
    private myColor: Color | null,
  ) {
    this.pos = parsePosition(full.fen) ?? Chess.default();
    this.serverFen = full.fen;
    this.turn = full.turn;
    this.status = full.status;
    this.lifecycle = full.lifecycle ?? "waiting";
    this.clockMode = full.clock_mode ?? "timed";
    this.clockRunning = full.clock_running === true;
    this.importedPlies = full.imported_ply_count ?? 0;
    this.latestFull = full;
    this.seenRevision = full.state_revision ?? 0;
    this.moves = [...full.moves];

    const boardEl = root.querySelector<HTMLElement>(".cg-board-wrap")!;
    this.cg = Chessground(boardEl, this.baseConfig(full));
    this.clock = new ClockUI(
      {
        white: root.querySelector<HTMLElement>('[data-clock="white"]')!,
        black: root.querySelector<HTMLElement>('[data-clock="black"]')!,
      },
      (color) => {
        // Warn only for my own low clock (spectators and the opponent's dip
        // shouldn't beep at me).
        if (color === this.myColor) play("low");
      },
    );

    this.rebuildNodes();
    this.viewPly = this.nodes.length - 1;
    this.replay = new MoveReplay(root,
      () => ({ current: this.viewPly, last: this.nodes.length - 1, blocked: this.awaitingEcho }),
      ply => this.goTo(ply, false));
    this.renderMoves();
    this.show();
    this.clock.sync(full.clock, full.turn, this.isClockRunning());
    if (full.status !== "started") this.showResult(full.status, full.winner ?? undefined, full.ratings ?? undefined);
    this.syncPlights(full);

    this.wireControls();
    this.lifecycleTimer = window.setInterval(() => this.renderLifecycle(), 1000);
    this.renderLifecycle();
    this.wireNav();
    this.onKey = (e) => this.handleKey(e);
    document.addEventListener("keydown", this.onKey);

    // Let a ready/turn navigation in another tab focus this board instead of
    // opening a duplicate (plan item 5).
    this.unregisterTab = registerBoard(this.id, () => window.focus());

    // React live when the pre-move preference is toggled (e.g. in Settings in
    // another tab): drop any queued pre-move if it was turned off, then re-render
    // so the board stops/starts accepting them.
    this.offPremove = onPremoveChange((on) => {
      if (!on) this.cg.cancelPremove();
      this.show();
    });

    if (this.myColor) {
      this.playConnection = new PlayConnection(() => this.playConnection?.seen("game",this.id,this.seenRevision));
      this.playConnection.start();
      this.socket = new GameSocket(this.id, full[this.myColor].id, {
        onEnvelope: (env) => this.onEnvelope(env),
        onStatus: (s) => { if (s === "live") document.getElementById("socket-notice")?.remove(); this.setConn(s); },
        onRefused: (message, retry) => { this.clearSocketNotice?.(); this.clearSocketNotice = showSocketNotice(message,retry); },
        onAvailability: (available) => { this.mutationsAvailable = available; this.show(); },
        onPending: (pending) => {
          this.awaitingEcho = pending;
          this.show();
        },
      });
      this.socket.version = full.status === "started" ? 0 : this.socket.version;
      this.socket.connect();
    } else {
      this.setConn("live");
      this.setNote(t("game.spectating"));
    }
  }

  private destroyed = false;

  destroy(): void {
    this.clearSocketNotice?.();
    if (this.lifecycleTimer) clearInterval(this.lifecycleTimer);
    if (this.destroyed) return;
    this.destroyed = true;
    this.socket?.close();
    this.playConnection?.close();
    this.clock.destroy();
    this.replay.destroy();
    this.cg.destroy();
    document.removeEventListener("keydown", this.onKey);
    if (this.goneTimer) clearInterval(this.goneTimer);
    this.unregisterTab();
    this.offPremove();
  }

  // --- board config ---------------------------------------------------

  private baseConfig(full: FullGame): CgConfig {
    return {
      fen: full.fen,
      orientation: this.myColor ?? "white",
      turnColor: full.turn,
      movable: { free: false, color: undefined, dests: new Map(), showDests: true },
      premovable: { enabled: this.myColor != null && isPremoveEnabled() },
      draggable: { showGhost: true },
      highlight: { lastMove: true, check: true },
      animation: { enabled: !matchMedia("(prefers-reduced-motion: reduce)").matches, duration: 180 },
      events: {},
    };
  }

  private isClockRunning(): boolean {
    return this.clockMode === "timed" && this.lifecycle === "active" && this.clockRunning && this.hasSocketSnapshot && this.connectionLive && this.latestFull?.mutations_available === true && this.status === "started";
  }

  private rebuildNodes(): void {
    this.nodes = buildReview(this.moves).nodes;
  }

  /** True when the board is showing the live tip (latest position). */
  private atLive(): boolean {
    return this.viewPly >= this.nodes.length - 1;
  }

  private currentFen(): string {
    return this.nodes[this.viewPly]?.fen ?? this.serverFen;
  }

  /** Render whatever ply `viewPly` points at: the interactive live tip, or a
   *  read-only past position when reviewing (unified with the analysis board). */
  private show(syncPosition = !this.awaitingEcho): void {
    const last = this.nodes.length - 1;
    this.viewPly = Math.max(0, Math.min(last, this.viewPly));
    if (this.viewPly === last) this.renderTip(syncPosition);
    else this.renderPast(this.viewPly);
    this.paintCapturedFor(this.currentFen());
    this.highlightMove();
    this.updateNavState();
    this.replay.update();
    for (const control of this.root.querySelectorAll<HTMLButtonElement>('[data-act="resign"], [data-act="draw"]')) {
      control.disabled = !this.mutationsAvailable || this.awaitingEcho || this.status !== "started"
        || (control.dataset.act === "draw" && this.lifecycle !== "active");
    }
    const back = this.root.querySelector<HTMLButtonElement>('[data-act="return"]');
    if (back) {
      // Asking twice, or again after a refusal before moving, is refused by
      // the server; do not offer what cannot work.
      const refused = !!this.myColor && this.returnRefused.includes(this.myColor);
      back.hidden = this.status !== "started";
      back.disabled = !this.mutationsAvailable || this.awaitingEcho || this.returnOffer !== undefined || refused;
    }
    this.renderLifecycle();
    const keyboardEntry = this.root.querySelector<HTMLElement>(".keyboard-entry");
    if (keyboardEntry) keyboardEntry.hidden = this.status !== "started";
    const moveAllowed = this.atLive() && this.lifecycle === "active" && this.status === "started" && this.myColor === this.turn && this.mutationsAvailable && !this.awaitingEcho;
    const entry = this.root.querySelector<HTMLInputElement>(".keyboard-move input");
    if (entry) entry.readOnly = !moveAllowed;
    const submit = this.root.querySelector<HTMLButtonElement>(".keyboard-move button");
    if (submit) submit.disabled = !moveAllowed;
    const description = t("game.boardStatus", { turn: t(`chess.${this.turn}`), color: this.myColor ? t(`chess.${this.myColor}`) : "", status: this.status === "started" ? t(this.lifecycle === "paused" ? "home.paused" : "game.live") : t(this.status === "returned" ? "game.inMessages" : "game.finished") });
    const board = this.root.querySelector<HTMLElement>(".cg-board-wrap")!;
    board.setAttribute("role", "img"); board.setAttribute("aria-label", description);
    const status = this.root.querySelector<HTMLElement>(".board-status");
    if (status && status.textContent !== description) status.textContent = description;
  }

  /** The live tip: interactive for a player in an ongoing game, else read-only. */
  private renderTip(syncPosition: boolean): void {
    const ongoing = this.status === "started";
    const interactive = ongoing && this.lifecycle === "active" && this.myColor != null && this.mutationsAvailable && !this.awaitingEcho;
    const myTurn = interactive && this.myColor === this.turn;
    const lastMove =
      this.moves.length > 0 ? uciSquares(this.moves[this.moves.length - 1].uci) : undefined;

    this.cg.set({
      // Pending/availability callbacks run synchronously during sendMove.
      // Lock input without undoing Chessground's already completed local move.
      // Authoritative snapshots and move events still reconcile the position.
      ...(syncPosition ? {
        fen: this.serverFen,
        turnColor: this.turn,
        lastMove,
        check: checkColor(this.pos),
      } : {}),
      viewOnly: !interactive,
      movable: {
        color: interactive ? (this.myColor as Color) : undefined,
        dests: myTurn ? legalDests(this.pos) : new Map(),
        events: { after: (orig, dest) => this.onUserMove(orig, dest) },
      },
      premovable: { enabled: interactive && isPremoveEnabled() },
    });

    if (myTurn && isPremoveEnabled()) {
      // If a premove was queued on the opponent's turn, fire it now.
      this.cg.playPremove();
    }
  }

  /** A past position — read-only, with that ply's last-move + check highlights. */
  private renderPast(ply: number): void {
    const node = this.nodes[ply];
    const pos = parsePosition(node.fen);
    this.cg.set({
      fen: node.fen,
      turnColor: node.turn,
      lastMove: node.uci ? uciSquares(node.uci) : undefined,
      check: pos ? checkColor(pos) : undefined,
      viewOnly: true,
      movable: { color: undefined, dests: new Map() },
      premovable: { enabled: false },
    });
  }

  private goTo(ply: number, manual = true): void {
    // Don't re-render (and thus revert an optimistic move) while it's in flight.
    if (this.awaitingEcho) return;
    if (manual) this.replay.pause();
    this.viewPly = ply;
    this.show();
  }

  private wireNav(): void {
    this.root.querySelector<HTMLElement>(".nav")?.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest("button[data-nav]");
      if (!btn) return;
      const last = this.nodes.length - 1;
      switch (btn.getAttribute("data-nav")) {
        case "first":
          this.goTo(0);
          break;
        case "prev":
          this.goTo(this.viewPly - 1);
          break;
        case "next":
          this.goTo(this.viewPly + 1);
          break;
        case "last":
          this.goTo(last);
          break;
      }
    });
    this.root.querySelector<HTMLElement>(".movelist")?.addEventListener("click", (e) => {
      const span = (e.target as HTMLElement).closest("[data-ply]");
      if (span) this.goTo(Number(span.getAttribute("data-ply")));
    });
  }

  private handleKey(e: KeyboardEvent): void {
    if (isTypingTarget(e.target)) return;
    const action = navKey(e);
    if (!action) return;
    e.preventDefault();
    const last = this.nodes.length - 1;
    this.goTo(
      action === "first" ? 0 : action === "last" ? last : action === "prev" ? this.viewPly - 1 : this.viewPly + 1,
    );
  }

  private updateNavState(): void {
    const last = this.nodes.length - 1;
    const disable = (nav: string, off: boolean) => {
      const b = this.root.querySelector<HTMLButtonElement>(`.nav [data-nav="${nav}"]`);
      if (b) b.disabled = off;
    };
    disable("first", this.viewPly <= 0);
    disable("prev", this.viewPly <= 0);
    disable("next", this.viewPly >= last);
    disable("last", this.viewPly >= last);
    // While an ongoing game is being rewound, flag the board as "reviewing".
    const reviewing = this.status === "started" && this.viewPly < last;
    this.root.querySelector(".board-col")?.classList.toggle("reviewing", reviewing);
  }

  private paintCapturedFor(fen: string): void {
    const cap = capturedFor(fen);
    const w = this.root.querySelector<HTMLElement>('[data-captured="white"]');
    const b = this.root.querySelector<HTMLElement>('[data-captured="black"]');
    if (w) paintCaptured(w, "white", cap);
    if (b) paintCaptured(b, "black", cap);
  }

  // --- making moves ---------------------------------------------------

  private onUserMove(orig: Key, dest: Key): void {
    this.replay.pause();
    if (!this.socket) return;
    if (isPromotion(this.pos, orig, dest)) {
      this.askPromotion(dest).then((role) => {
        if (!role) {
          // Cancelled: restore the authoritative board.
          this.show();
          return;
        }
        this.commitMove(orig + dest + role);
      });
    } else {
      this.commitMove(orig + dest);
    }
  }

  private commitMove(uci: string): void {
    this.replay.pause();
    const think = performance.now() - this.lastMoveShownAt;
    if (!this.socket!.sendMove(uci, think)) {
      this.show();
      this.setNote("Checking the current game before sending another action…");
      return;
    }
    void renewGuestOnActivity().catch(error=>this.setNote(error instanceof Error?error.message:'Could not renew this browser session.'));
    // Lock the board until the server echoes the move: chessground already shows
    // the piece optimistically, but `serverFen`/`turn` are still pre-move, so any
    // re-render (e.g. a nav keypress) would revert it and re-open the board for a
    // second move. Freeze navigation until the echo (`applyMove`) lands.
    this.awaitingEcho = true;
    this.cg.set({ movable: { color: undefined, dests: new Map() } });
  }

  // --- incoming events ------------------------------------------------

  private onEnvelope(env: ServerEnvelope): void {
    switch (env.t) {
      case "activity":
        this.serverTimeOffset = env.d.server_time - Date.now() / 1000;
        if (this.latestFull) {
          this.latestFull.white_active_until = env.d.white_active_until;
          this.latestFull.black_active_until = env.d.black_active_until;
        }
        this.renderLifecycle();
        break;
      case "full":
        this.applyFull(env.d);
        break;
      case "move":
        this.applyMove(env.d);
        break;
      case "end":
        this.applyEnd(env.d);
        break;
      case "gone":
        this.applyGone(env.d);
        break;
      case "presence":
        this.applyPresence(env.d);
        break;
      case "draw-offer":
        this.applyDrawOffer(env.d.by);
        break;
      case "return-offer":
        this.applyReturnOffer(env.d.by, env.d.refused);
        this.show();
        break;
      case "rematch-offer":
        this.applyRematchOffer(env.d);
        break;
      case "rematch":
        navigate(`/game/${env.d.game_id}`);
        break;
      case "chat":
        this.appendChat(env.d);
        break;
      case "error":
        this.setNote(env.d);
        break;
    }
    if (env.t === "full" || env.t === "move" || env.t === "end" || env.t === "return-offer") {
      this.seenRevision = env.d.state_revision ?? this.seenRevision;
      this.playConnection?.seen("game",this.id,this.seenRevision);
    }
  }

  private applyFull(full: FullGame): void {
    this.replay.pause();
    this.hasSocketSnapshot = true;
    this.latestFull = full;
    this.lifecycle = full.lifecycle ?? "waiting";
    this.clockMode = full.clock_mode ?? "timed";
    this.clockRunning = full.clock_running === true;
    this.importedPlies = full.imported_ply_count ?? 0;
    this.awaitingEcho = this.socket?.hasPending ?? false;
    // Only GameSocket's snapshot-and-receipt gate can enable input.
    if (full.mutations_available !== true) this.setNote(t("game.unavailable"));
    this.pos = parsePosition(full.fen) ?? Chess.default();
    this.serverFen = full.fen;
    this.turn = full.turn;
    this.status = full.status;
    this.moves = [...full.moves];
    this.rebuildNodes();
    this.viewPly = this.nodes.length - 1;
    this.renderMoves();
    this.show(true);
    this.clock.sync(full.clock, full.turn, this.isClockRunning());
    this.lastMoveShownAt = performance.now();
    if (full.status !== "started") {
      this.showResult(full.status, full.winner ?? undefined, full.ratings ?? undefined);
    } else {
      this.hideResult();
    }
    this.applyDrawOffer(full.draw_offer ?? undefined);
    this.applyReturnOffer(full.match?.return_offer ?? undefined, full.match?.return_refused);
    this.syncPlights(full);
    // A rematch already agreed before we (re)loaded: offer a one-click jump in.
    if (full.rematch_game_id) this.showRematchReady(full.rematch_game_id);
  }

  private applyMove(m: MoveOut): void {
    const next = parsePosition(m.fen);
    if (!next) return;
    // A successfully played opponent move is definitive proof that they are
    // back. Clear a stale claim banner even if its earlier reconnect-clear
    // event was missed while this tab's socket was recovering.
    const mover = this.turn;
    if (this.myColor && mover !== this.myColor) this.clearGone();
    // Follow the live game only if we were already at the tip; if the player is
    // reviewing an earlier move, leave them there while the tip advances.
    const wasAtTip = this.atLive();
    this.awaitingEcho = this.socket?.hasPending ?? false;
    this.pos = next;
    this.serverFen = m.fen;
    this.turn = next.turn as Color;
    this.moves.push({ uci: m.uci, san: m.san });
    this.clockRunning = m.clock !== null;
    if (this.latestFull) this.latestFull.start_deadline = null;
    this.rebuildNodes();
    this.renderMoves();
    this.clock.sync(m.clock, this.turn, this.isClockRunning());
    this.lastMoveShownAt = performance.now();
    this.clearDrawOfferUI();
    if (wasAtTip) this.viewPly = this.nodes.length - 1;
    this.show(true);
    this.playMoveSound(m.san);
  }

  /** Pick a sound from the move's SAN: mate is handled by the `end` event, so
   *  here check → check, a capture ('x') → capture, otherwise a plain move. */
  private playMoveSound(san: string): void {
    if (san.includes("#")) return; // the end flourish covers mate
    if (san.includes("+")) play("check");
    else if (san.includes("x")) play("capture");
    else play("move");
  }

  private applyEnd(e: EndOut): void {
    this.status = e.status;
    this.awaitingEcho = false;
    this.clock.stop();
    this.clearGone();
    // The game is over — a standing draw offer no longer applies; clear its
    // banner (the accept path ends the game without a separate clear event).
    this.clearDrawOfferUI();
    this.applyReturnOffer();
    // Snap to the final position and lock the board into review mode.
    this.viewPly = this.nodes.length - 1;
    this.show();
    this.showResult(e.status, e.winner, e.ratings);
    play("end");
  }

  private applyGone(g: GoneOut): void {
    // A message about *my* colour going gone is not actionable; ignore it.
    if (!this.myColor || g.color === this.myColor) {
      this.clearGone();
      return;
    }
    if (g.claim_in_seconds === 0) {
      this.clearGone(); // opponent came back
      return;
    }
    this.startGoneCountdown(g.claim_in_seconds);
  }

  // --- per-player liveness dots (plan item 2) -------------------------

  /** A live presence event for one colour drives that player's dot. My own dot
   *  is driven by the socket status (see {@link setConn}) rather than the
   *  server, since the server can't reach me while my socket is down. */
  private applyPresence(p: PresenceOut): void {
    if (this.myColor && p.color === this.myColor) return;
    this.setPlight(p.color, p.state);
  }

  /** Paint both dots from a full snapshot's per-colour state. */
  private syncPlights(full: FullGame): void {
    this.setPlight("white", full.white_state ?? "gone");
    this.setPlight("black", full.black_state ?? "gone");
    // My own dot mirrors my live socket, not the snapshot.
    if (this.myColor) this.setPlight(this.myColor, this.socket ? "live" : "gone");
  }

  private setPlight(color: Color, state: PeerState): void {
    const dot = this.root.querySelector<HTMLElement>(`.plight[data-plight="${color}"]`);
    if (!dot) return;
    dot.className = `plight ${state}`;
    dot.title = state === "live" ? t("game.online") : state === "away" ? t("game.awayReconnect") : t("game.offline");
  }

  private applyDrawOffer(by?: Color): void {
    const banner = this.root.querySelector<HTMLElement>(".draw-offer")!;
    if (by && this.myColor && by !== this.myColor) {
      banner.hidden = false;
      banner.innerHTML = t("game.drawOffered");
      const yes = mkBtn(t("game.accept"), "primary");
      const no = mkBtn(t("game.decline"), "ghost");
      yes.onclick = () => this.socket?.offerOrAcceptDraw();
      no.onclick = () => this.socket?.declineDraw();
      banner.append(yes, no);
    } else if (by && by === this.myColor) {
      banner.hidden = false;
      banner.textContent = t("game.drawWaiting");
    } else {
      this.clearDrawOfferUI();
    }
  }

  private clearDrawOfferUI(): void {
    const banner = this.root.querySelector<HTMLElement>(".draw-offer")!;
    banner.hidden = true;
    banner.replaceChildren();
  }

  // --- continue in Messages -------------------------------------------

  /** A request to take the match back to Messages: the opponent answers it,
   *  the asker waits or withdraws. A refusal holds until the asker has moved. */
  private applyReturnOffer(by?: Color, refused: Color[] = []): void {
    this.returnOffer = by;
    this.returnRefused = refused;
    const banner = this.root.querySelector<HTMLElement>(".return-offer");
    if (!banner) return;
    banner.replaceChildren();
    banner.hidden = false;
    if (by && this.myColor && by !== this.myColor) {
      const yes = mkBtn(t("game.returnAgree"), "primary");
      const no = mkBtn(t("game.returnNotNow"), "ghost");
      yes.dataset.act = "return-accept";
      no.dataset.act = "return-decline";
      yes.onclick = () => this.socket?.acceptReturn();
      no.onclick = () => this.socket?.declineReturn();
      banner.append(document.createTextNode(t("game.returnAsked")), yes, no);
    } else if (by && by === this.myColor) {
      const withdraw = mkBtn(t("game.returnWithdraw"), "ghost");
      withdraw.dataset.act = "return-withdraw";
      withdraw.onclick = () => this.socket?.withdrawReturn();
      banner.append(document.createTextNode(t("game.returnWaiting")), withdraw);
    } else if (this.myColor && refused.includes(this.myColor) && this.status === "started") {
      banner.textContent = t("game.returnRefusedHint");
    } else {
      banner.hidden = true;
    }
  }

  // --- rematch --------------------------------------------------------

  /** One side has asked for a rematch. Show a persistent banner: the offerer
   *  waits, the opponent gets a one-click Accept (both send `rematch-yes`; the
   *  server pairs them and hands back the new game via a `rematch` event). */
  private applyRematchOffer(by: Color): void {
    if (!this.myColor) return; // spectators have nothing to accept
    const banner = this.root.querySelector<HTMLElement>(".rematch-offer")!;
    banner.hidden = false;
    banner.replaceChildren();
    if (by === this.myColor) {
      banner.textContent = t("game.rematchWaiting");
      const withdraw = mkBtn(t("game.withdrawConfirmation"), "ghost");
      withdraw.onclick = () => this.socket?.withdrawRematch();
      banner.append(withdraw);
    } else {
      banner.append(document.createTextNode(t("game.rematchWants")));
      const yes = mkBtn(t("game.acceptRematch"), "primary");
      yes.onclick = () => this.socket?.rematch();
      banner.append(yes);
    }
  }

  /** A rematch was already agreed (seen in a full snapshot on (re)load) — jump in. */
  private showRematchReady(gameId: string): void {
    const banner = this.root.querySelector<HTMLElement>(".rematch-offer")!;
    banner.hidden = false;
    banner.replaceChildren();
    banner.append(document.createTextNode(t("game.rematchReady")));
    const join = mkBtn(t("game.join"), "primary");
    join.onclick = () => navigate(`/game/${gameId}`);
    banner.append(join);
  }

  // --- gone / claim ---------------------------------------------------

  private startGoneCountdown(seconds: number): void {
    if (this.clockMode === "untimed" || this.lifecycle !== "active") return;
    this.clearGone();
    const banner = this.root.querySelector<HTMLElement>(".gone-banner")!;
    let left = seconds;
    const render = () => {
      banner.hidden = false;
      banner.replaceChildren();
      if (left > 0) {
        banner.append(document.createTextNode(t("game.oppLeftClaim", { n: left })));
      } else {
        const btn = mkBtn(t("game.claimWin"), "primary");
        btn.onclick = () => {
          // The claim action has been taken; remove the now-obsolete prompt
          // immediately instead of waiting for the terminal server event.
          this.clearGone();
          this.socket?.claimWin();
        };
        banner.append(document.createTextNode(t("game.oppLeft")), btn);
      }
    };
    render();
    this.goneTimer = window.setInterval(() => {
      left -= 1;
      if (left < 0) {
        clearInterval(this.goneTimer);
        this.goneTimer = undefined;
      }
      render();
    }, 1000);
  }

  private clearGone(): void {
    if (this.goneTimer) clearInterval(this.goneTimer);
    this.goneTimer = undefined;
    const banner = this.root.querySelector<HTMLElement>(".gone-banner");
    if (banner) {
      banner.hidden = true;
      banner.replaceChildren();
    }
  }

  // --- controls / chat ------------------------------------------------

  private wireControls(): void {
    this.root.querySelector(".side")!.append(boardSettings(this.status === "started" ? "play" : "replay"));
    this.root.querySelector<HTMLFormElement>(".keyboard-move")?.addEventListener("submit", event => {
      event.preventDefault();
      if (!this.atLive() || this.lifecycle !== "active" || this.status !== "started" || this.myColor !== this.turn || !this.mutationsAvailable || this.awaitingEcho) return;
      const input = this.root.querySelector<HTMLInputElement>(".keyboard-move input")!;
      const value = input.value.trim();
      const move = parseSan(this.pos, value) ?? parseUci(value);
      if (!move || !("from" in move) || !this.pos.isLegal(move)) { this.setNote(t("game.invalidTypedMove")); return; }
      if (this.pos.board.getRole(move.from) === "king" && Math.abs(move.to - move.from) > 1 && Math.floor(move.to / 8) === Math.floor(move.from / 8)) {
        move.to = move.from + (move.to > move.from ? 2 : -2);
      }
      this.commitMove(makeUci(move)); input.value = "";
    });
    this.root.querySelector<HTMLButtonElement>('[data-act="pause"]')?.addEventListener("click", () => this.socket?.pause());
    this.root.querySelector<HTMLButtonElement>('[data-act="resume"]')?.addEventListener("click", () => this.socket?.resumeReady());
    this.root.querySelector<HTMLButtonElement>('[data-act="withdraw-resume"]')?.addEventListener("click", () => this.socket?.withdrawResume());
    this.root.querySelector<HTMLElement>(".mute-slot")?.append(muteButton());

    const resign = this.root.querySelector<HTMLButtonElement>('[data-act="resign"]');
    const draw = this.root.querySelector<HTMLButtonElement>('[data-act="draw"]');
    resign?.addEventListener("click", async () => {
      if (await confirmDialog(t("game.resignConfirm"), { danger: true })) this.socket?.resign();
    });
    draw?.addEventListener("click", () => this.socket?.offerOrAcceptDraw());
    // One action. Without a running clock the match returns at once; on a
    // running clock this asks the opponent, who sees Agree / Not now.
    this.root.querySelector<HTMLButtonElement>('[data-act="return"]')
      ?.addEventListener("click", () => this.socket?.requestReturn());

    const chatForm = this.root.querySelector<HTMLFormElement>(".chat form");
    chatForm?.addEventListener("submit", (e) => {
      e.preventDefault();
      const input = chatForm.querySelector("input")!;
      const text = input.value.trim();
      if (text && this.socket) {
        this.socket.chat(text);
        input.value = "";
      }
    });
  }

  private appendChat(c: ChatOut): void {
    const log = this.root.querySelector<HTMLElement>(".chat-log")!;
    const line = document.createElement("div");
    line.className = "chat-line";
    line.innerHTML = `<strong>${esc(c.u)}</strong> ${esc(c.text)}`;
    log.append(line);
    log.scrollTop = log.scrollHeight;
  }

  // --- move list ------------------------------------------------------

  private renderMoves(): void {
    const list = this.root.querySelector<HTMLElement>(".movelist")!;
    list.replaceChildren();
    for (let i = 0; i < this.moves.length; i += 2) {
      const row = document.createElement("div");
      row.className = "move-row";
      const mn = document.createElement("span");
      mn.className = "mn";
      mn.textContent = `${i / 2 + 1}.`;
      row.append(mn, this.moveSpan(i));
      row.append(this.moves[i + 1] ? this.moveSpan(i + 1) : blankSpan());
      list.append(row);
    }
    this.highlightMove();
  }

  /** One half-move span; `data-ply` is the node index it navigates to. */
  private moveSpan(moveIdx: number): HTMLElement {
    const span = document.createElement("span");
    span.className = "san";
    span.setAttribute("data-ply", String(moveIdx + 1));
    span.textContent = this.moves[moveIdx].san;
    if (moveIdx < this.importedPlies) span.title = t("game.playedInMessages");
    if (moveIdx === this.importedPlies) span.title = t("game.onlineBoundary");
    return span;
  }

  private renderLifecycle(): void {
    const banner = this.root.querySelector<HTMLElement>(".lifecycle-banner");
    const full = this.latestFull;
    if (!banner || !full) return;
    const paused = this.lifecycle === "paused" && this.status === "started";
    const active = this.lifecycle === "active" && this.status === "started";
    const now = Date.now() / 1000 + this.serverTimeOffset;
    const myReady = this.myColor === "white" ? full.white_ready_until : full.black_ready_until;
    const ready = (myReady ?? 0) > now;
    if (this.status !== "started") {
      if (full.rematch_game_id) this.showRematchReady(full.rematch_game_id);
      else if ((full.white_ready_until ?? 0) > now) this.applyRematchOffer("white");
      else if ((full.black_ready_until ?? 0) > now) this.applyRematchOffer("black");
      else { const rematch = this.root.querySelector<HTMLElement>(".rematch-offer"); if (rematch) rematch.hidden = true; }
    }
    let message = "";
    if (paused) {
      message = full.pause_reason === "server_restart" || full.pause_reason === "storage_recovery" || full.pause_reason === "actor_recovery"
        ? t("game.pausedInterruption")
        : t("game.pausedConsent");
      if (this.clockMode === "timed") message += " " + t("game.savedClocks");
      if (ready) message += " " + t("game.readyExpires", { seconds: Math.ceil(myReady! - now) });
    } else if (active && full.start_deadline) {
      message = t("game.firstMoveDeadline", { seconds: Math.max(0, Math.ceil(full.start_deadline - now)) });
    } else if (active && this.clockMode === "untimed") {
      message = t("game.untimedAbsence");
      const until = Math.min(full.white_active_until ?? Infinity, full.black_active_until ?? Infinity);
      if (until - now < 90) {
        const side = (full.white_active_until ?? Infinity) <= (full.black_active_until ?? Infinity) ? t("chess.white") : t("chess.black");
        message = t("game.awayCountdown", { side, seconds: Math.max(0, Math.ceil(until - now)) });
      }
    }
    if (this.importedPlies > 0) message += " " + t("game.importedCount", { count: this.importedPlies });
    banner.textContent = message;
    banner.hidden = !message;
    const state = { pause: active && this.clockMode === "untimed", resume: paused && !ready, "withdraw-resume": paused && ready };
    for (const [action, shown] of Object.entries(state)) {
      const btn = this.root.querySelector<HTMLButtonElement>(`[data-act="${action}"]`);
      if (btn) { btn.hidden = !shown; btn.disabled = !this.mutationsAvailable || this.awaitingEcho; }
    }
  }

  private highlightMove(): void {
    const list = this.root.querySelector<HTMLElement>(".movelist");
    if (!list) return;
    list.querySelectorAll(".san.cur").forEach((s) => s.classList.remove("cur"));
    const cur = list.querySelector<HTMLElement>(`[data-ply="${this.viewPly}"]`);
    if (cur) {
      cur.classList.add("cur");
      scrollIntoListView(list, cur);
    }
  }

  // --- result / promotion / misc UI -----------------------------------

  /** The finished-game card: a bold outcome (score + word), the reason, an
   *  optional rating delta, then the actions. Reviewing stays on this screen —
   *  the nav controls scrub the game in place (Analyze opens the engine board). */
  private showResult(status: string, winner?: Color, ratings?: RatingsOut): void {
    const box = this.root.querySelector<HTMLElement>(".result")!;
    box.hidden = false;
    box.replaceChildren();

    // Not a result: the segment ended and the match goes on in Messages.
    if (status === "returned") {
      const head = document.createElement("div");
      head.className = "result-head returned";
      const word = document.createElement("span");
      word.className = "result-word";
      word.textContent = t("game.returnedHead");
      head.append(word);
      const body = document.createElement("p");
      body.className = "result-reason";
      body.textContent = t("game.returnedBody");
      const actions = document.createElement("div");
      actions.className = "result-actions";
      const home = mkBtn(t("common.home"), "ghost");
      home.onclick = () => navigate("/");
      actions.append(home);
      box.append(head, body, actions);
      return;
    }

    // Spectators have no "you", so a decisive game gets the neutral tint.
    const outcome: "won" | "lost" | "draw" =
      !this.myColor || !winner ? "draw" : winner === this.myColor ? "won" : "lost";
    const word = this.myColor
      ? outcome === "won"
        ? t("result.youWon")
        : outcome === "lost"
          ? t("result.youLost")
          : t("result.drawWord")
      : winner
        ? t("result.wins", { who: capColor(winner) })
        : t("result.drawWord");

    const head = document.createElement("div");
    head.className = `result-head ${outcome}`;
    const score = document.createElement("span");
    score.className = "result-score";
    score.textContent = status === "aborted" && !winner ? "*" : scoreStr(winner);
    const w = document.createElement("span");
    w.className = "result-word";
    w.textContent = status === "aborted" && !winner ? t("game.unfinished") : word;
    head.append(score, w);
    box.append(head);

    const reason = reasonText(status);
    if (reason) {
      const r = document.createElement("p");
      r.className = "result-reason";
      r.textContent = reason;
      box.append(r);
    }

    // Rating change for a rated game (my side).
    if (ratings && this.myColor) {
      const mine = this.myColor === "white" ? ratings.white : ratings.black;
      const diff = this.myColor === "white" ? ratings.white_diff : ratings.black_diff;
      const rl = document.createElement("p");
      rl.className = `rating-line ${diff > 0 ? "up" : diff < 0 ? "down" : ""}`;
      const sign = diff > 0 ? "+" : "";
      rl.textContent = `${mine} (${sign}${diff})`;
      box.append(rl);
    }

    const actions = document.createElement("div");
    actions.className = "result-actions";
    const analyze = mkBtn(t("game.analyze"), "ghost");
    analyze.onclick = () => navigate(`/analysis/game/${this.id}`);
    if (this.myColor) {
      const rematch = mkBtn(t("game.rematch"), "primary");
      rematch.onclick = () => this.socket?.rematch();
      const home = mkBtn(t("common.home"), "ghost");
      home.onclick = () => navigate("/");
      if (this.clockMode === "timed") actions.append(rematch);
      actions.append(analyze, home);
    } else {
      const home = mkBtn(t("common.home"), "ghost");
      home.onclick = () => navigate("/");
      actions.append(analyze, home);
    }
    box.append(actions);
  }

  private hideResult(): void {
    const box = this.root.querySelector<HTMLElement>(".result");
    if (box) box.hidden = true;
  }

  private askPromotion(dest: Key): Promise<string | null> {
    return new Promise((resolve) => {
      const overlay = this.root.querySelector<HTMLElement>(".promotion")!;
      // `this.pos` is still the pre-move position, so its side-to-move is the
      // side that's promoting — render the choices in that colour.
      const card = promotionCard(this.pos.turn as Color, (code) => {
        overlay.hidden = true;
        resolve(code);
      });
      overlay.replaceChildren(card);
      overlay.hidden = false;
      void dest;
    });
  }

  private setConn(status: "live" | "reconnecting"): void {
    this.connectionLive = status === "live";
    if (!this.connectionLive) { this.hasSocketSnapshot = false; this.clock.stop(); }
    const el = this.root.querySelector<HTMLElement>(".conn")!;
    el.className = `conn ${status === "live" ? "ok" : "bad"}`;
    el.textContent = status === "live" ? t("game.live") : t("game.reconnecting");
    // My own dot follows my socket immediately: amber while reconnecting.
    if (this.myColor) this.setPlight(this.myColor, status === "live" ? "live" : "away");
  }

  private setNote(text: string): void {
    const el = this.root.querySelector<HTMLElement>(".note")!;
    el.textContent = text;
    if (text) window.setTimeout(() => (el.textContent === text ? (el.textContent = "") : null), 4000);
  }
}

// ---------------------------------------------------------------------------
// Static layout
// ---------------------------------------------------------------------------

function buildLayout(full: FullGame, myColor: Color | null): HTMLElement {
  const top = myColor === "white" ? full.black : full.white;
  const bottom = myColor === "black" ? full.black : full.white;
  const topColor: Color = myColor === "white" ? "black" : "white";
  const bottomColor: Color = myColor === "black" ? "black" : "white";

  const el = h(`
    <main class="game">
      <div class="board-col">
        <div class="cg-board-wrap"></div>
        <div class="promotion" hidden></div>
      </div>
      <aside class="side">
        <div class="side-top">
          <span class="conn bad">…</span>
          <span class="tc">${full.clock_mode === "untimed" ? t("game.untimedTerms") : `${esc(full.speed)} · ${esc(tcLabel(full))} · ${full.rated ? t("chess.rated") : t("clock.unrated")}`}</span>
          <span class="mute-slot"></span>
        </div>

        <div class="player">
          <div class="pmeta">
            <span class="pname"><span class="plight" data-plight="${topColor}" title="${t("game.connection")}"></span>${esc(top.name)}</span>
            <span class="captured" data-captured="${topColor}"></span>
          </div>
          <span class="clock" data-clock="${topColor}">--</span>
        </div>

        <div class="movelist"></div>
        <div class="nav-slot"></div>
        <p class="board-status fine" role="status" aria-live="polite"></p>
        ${myColor ? `<details class="keyboard-entry"><summary>${t("game.enterMove")}</summary><form class="keyboard-move"><label>${t("game.moveNotation")}<input maxlength="12" autocomplete="off" autocapitalize="off" spellcheck="false" placeholder="Nf3 / g1f3" /></label><button class="btn ghost" type="submit">${t("game.playMove")}</button></form></details>` : ""}

        <div class="player">
          <div class="pmeta">
            <span class="pname"><span class="plight" data-plight="${bottomColor}" title="${t("game.connection")}"></span>${esc(bottom.name)}</span>
            <span class="captured" data-captured="${bottomColor}"></span>
          </div>
          <span class="clock" data-clock="${bottomColor}">--</span>
        </div>

        <div class="gone-banner" hidden></div>
        <p class="lifecycle-banner" role="status" aria-live="polite" hidden></p>
        <div class="draw-offer" hidden></div>
        <div class="return-offer" role="status" aria-live="polite" hidden></div>
        <p class="note" role="status" aria-live="polite"></p>

        <div class="controls">
          ${myColor ? `<button class="btn ghost" data-act="draw" type="button">${t("game.offerDraw")}</button>
          <button class="btn ghost" data-act="resign" type="button">${t("game.resign")}</button>
          <button class="btn ghost" data-act="pause" type="button" hidden>${t("game.pauseGame")}</button>
          <button class="btn primary" data-act="resume" type="button" hidden>${t("game.resumeReady")}</button>
          <button class="btn ghost" data-act="withdraw-resume" type="button" hidden>${t("game.withdrawConfirmation")}</button>` : ""}
          ${myColor && full.match ? `<button class="btn ghost" data-act="return" type="button">${t("game.continueInMessages")}</button>` : ""}
        </div>

        <div class="result" hidden></div>
        <div class="rematch-offer" hidden></div>

        <div class="chat">
          <div class="chat-log"></div>
          ${myColor ? `<form><input maxlength="280" placeholder="${t("game.saySomething")}" autocomplete="off" /></form>` : ""}
        </div>
      </aside>
    </main>
  `);
  el.querySelector<HTMLElement>(".nav-slot")!.append(navControls());
  return el;
}

function tcLabel(full: FullGame): string {
  return `${full.base_cs / 6000}+${full.inc_cs / 100}`;
}

function mkBtn(text: string, kind: "primary" | "ghost"): HTMLButtonElement {
  const b = document.createElement("button");
  b.className = `btn ${kind}`;
  b.type = "button";
  b.textContent = text;
  return b;
}

/** The chess score for a result: 1–0 / 0–1 / ½–½. */
function scoreStr(winner?: Color): string {
  return winner === "white" ? "1–0" : winner === "black" ? "0–1" : "½–½";
}

/** How a game ended, from its terminal status (EndOut carries no sub-reason for
 *  draws, so those show just the score). */
function reasonText(status: string): string {
  const map: Record<string, string> = {
    mate: t("reason.checkmate"),
    resign: t("reason.resignation"),
    timeout: t("reason.onTime"),
    aborted: t("reason.abandoned"),
  };
  return map[status] ?? "";
}

function capColor(c: Color): string {
  return c === "white" ? t("chess.white") : t("chess.black");
}

function blankSpan(): HTMLElement {
  const s = document.createElement("span");
  s.className = "san";
  return s;
}

function notice(body: string): HTMLElement {
  return h(`
    <main class="card">
      <h1 class="brand" aria-label="${BRAND_NAME}">${brandLockup}</h1>
      <p class="msg bad">${esc(body)}</p>
      <p><a class="btn ghost" data-link href="/">${t("common.backHome")}</a></p>
    </main>
  `);
}
